"use client";

import { TipTap } from "@/lib/tiptap/types";
import Icon from "@/components/icon";
import Image from "next/image";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ReelStatsTracker } from "@/services/quickchannel/stats";

export type ReelItem = {
  title: string;
  content?: string[] | TipTap.Document[];
  thumbnail: string | null;
  hlsUrl: string | null;
  mp4Url: string | null;
  publishedAt: string | null;
  videoId: string;
  statsToken: string | null;
};

function extractText(content?: string[] | TipTap.Document[]): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (!content.length) return "";
  const first = content[0];
  if (typeof first === "string") return first;
  const doc = first as TipTap.Document;
  return doc.content
    .flatMap((block) => {
      const b = block as { content?: Array<{ type: string; text?: string }> };
      return b.content
        ? b.content.filter((n) => n.type === "text" && n.text).map((n) => n.text as string)
        : [];
    })
    .join("");
}

function formatPublishedAt(dateStr: string): string {
  const date = new Date(dateStr);
  const time = new Intl.DateTimeFormat("sv-SE", { timeStyle: "short" }).format(date);
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayMidnight = new Date(todayMidnight);
  yesterdayMidnight.setDate(yesterdayMidnight.getDate() - 1);
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (dateMidnight.getTime() === todayMidnight.getTime()) return `Publicerad i dag ${time}`;
  if (dateMidnight.getTime() === yesterdayMidnight.getTime()) return `Publicerad i går ${time}`;
  const formatted = new Intl.DateTimeFormat("sv-SE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return `Publicerad ${formatted} ${time}`;
}

type SlideProps = {
  item: ReelItem;
  isActive: boolean;
  preload?: "auto" | "metadata" | "none";
  isMuted?: boolean;
  onVideoRef?: (el: HTMLVideoElement | null) => void;
  onEnded?: () => void;
};

function ReelSlide({ item, isActive, preload = "none", isMuted = false, onVideoRef, onEnded }: SlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tapStartY = useRef<number | null>(null);
  const tapMoved = useRef(false);

  // Track whether this slide was already active to distinguish genuine navigation from micro-scroll oscillation.
  const wasActiveRef = useRef(false);

  // Reports view/engagement to Screen9's Stats API so plays through our native <video>
  // still count in Quickchannel's dashboard. No-ops (via optional chaining below) when
  // statsToken is missing — never blocks playback.
  const statsTracker = useMemo(
    () => (item.statsToken ? new ReelStatsTracker(item.statsToken, item.videoId) : null),
    [item.statsToken, item.videoId],
  );

  useEffect(() => {
    return () => statsTracker?.finish();
  }, [statsTracker]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActive) {
      if (!wasActiveRef.current) {
        video.currentTime = 0;
        if (progressBarRef.current) progressBarRef.current.style.width = "0%";
      }
      wasActiveRef.current = true;
      video.play().catch(() => {});
      setIsPaused(false);
    } else {
      wasActiveRef.current = false;
      video.pause();
      setIsPaused(false);
      statsTracker?.finish();
    }
  }, [isActive, statsTracker]);

  // React's `muted` prop is unreliable — sync imperatively.
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = isMuted;
  }, [isMuted]);

  useEffect(() => {
    const handleVisibility = () => {
      const video = videoRef.current;
      if (!video) return;
      if (document.hidden) video.pause();
      else if (isActive && !isPaused) video.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isActive, isPaused]);

  useEffect(() => {
    return () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); };
  }, []);

  const togglePause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play(); setIsPaused(false); }
    else { video.pause(); setIsPaused(true); }
    setShowHint(true);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setShowHint(false), 800);
  };

  return (
    <div className="absolute inset-0">
      <video
        ref={(el) => {
          (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
          onVideoRef?.(el);
        }}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: videoReady ? 1 : 0, transition: "opacity 0.4s ease" }}
        playsInline
        preload={preload}
        onCanPlay={() => setVideoReady(true)}
        onEnded={onEnded}
        onPlay={() => statsTracker?.onPlay(videoRef.current?.currentTime ?? 0)}
        onPause={() => statsTracker?.onPause(videoRef.current?.currentTime ?? 0)}
        onTimeUpdate={() => {
          const v = videoRef.current;
          if (v?.duration && progressBarRef.current)
            progressBarRef.current.style.width = `${(v.currentTime / v.duration) * 100}%`;
          if (v) statsTracker?.onTimeUpdate(v.currentTime);
        }}
      >
        {item.hlsUrl && <source src={item.hlsUrl} type="application/vnd.apple.mpegurl" />}
        {item.mp4Url && <source src={item.mp4Url} type="video/mp4" />}
      </video>

      {item.thumbnail && (
        // eslint-disable-next-line @next/next/no-img-element -- inside overlay, not LCP-relevant
        <img
          src={item.thumbnail}
          alt=""
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            objectFit: "cover", maxWidth: "none", maxHeight: "none",
            opacity: videoReady ? 0 : 1, transition: "opacity 0.4s ease",
          }}
        />
      )}

      <div className="absolute top-0 left-0 right-0 z-20" style={{ height: 3, background: "white" }}>
        <div ref={progressBarRef} style={{ width: "0%", height: "100%", background: "rgb(229, 7, 53)", transition: "width 0.15s linear" }} />
      </div>

      {/* touch-action: pan-y lets the browser handle scroll natively while taps still fire onClick */}
      <div
        className="absolute inset-0 z-10"
        style={{ touchAction: "pan-y" }}
        onTouchStart={(e) => { tapStartY.current = e.touches[0].clientY; tapMoved.current = false; }}
        onTouchMove={(e) => { if (tapStartY.current !== null && Math.abs(e.touches[0].clientY - tapStartY.current) > 8) tapMoved.current = true; }}
        onClick={() => { if (tapMoved.current) { tapMoved.current = false; return; } togglePause(); }}
      />

      {showHint && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
          style={{ animation: "reels-hint-fade 0.8s ease forwards" }}>
          <div style={{ background: "rgba(0,0,0,0.45)", borderRadius: "50%", padding: 18, display: "flex" }}>
            {isPaused
              ? <svg width="36" height="36" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
              : <svg width="36" height="36" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>}
          </div>
        </div>
      )}

      <div className="absolute bottom-6 left-4 right-4 z-20 pointer-events-none">
        {item.content && (
          <p className="font-bold text-lg drop-shadow line-clamp-3 mb-1" style={{ color: "white" }}>
            {extractText(item.content)}
          </p>
        )}
        {item.publishedAt && (
          <p className="text-sm drop-shadow" style={{ color: "rgba(255,255,255,0.6)" }}>
            {formatPublishedAt(item.publishedAt)}
          </p>
        )}
      </div>
    </div>
  );
}

type Props = { items: ReelItem[]; galleryCount?: number };

export default function ReelsViewer({ items, galleryCount }: Props) {
  const galleryItems = galleryCount ? items.slice(0, galleryCount) : items;
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const galleryRef = useRef<HTMLDivElement>(null);
  const galleryCardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [galleryPos, setGalleryPos] = useState<"left" | "middle" | "right">("left");
  const [galleryInView, setGalleryInView] = useState(false);
  const [galleryPreviewIndex, setGalleryPreviewIndex] = useState(0);

  const close = () => setOverlayOpen(false);

  const scrollGallery = (dir: "left" | "right") => {
    const el = galleryRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "right" ? el.clientWidth : -el.clientWidth, behavior: "smooth" });
  };

  const openAt = (i: number) => {
    setVisibleIndex(i);
    setOverlayOpen(true);
  };

  const scrollToIndex = (i: number, behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: i * el.clientHeight, behavior });
  };

  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;
    const update = () => {
      const atRight = Math.floor(el.scrollWidth - el.clientWidth - el.scrollLeft) <= 0;
      const atLeft = el.scrollLeft <= 0;
      setGalleryPos(atRight ? "right" : atLeft ? "left" : "middle");
    };
    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, []);

  // Detect when the gallery strip itself enters the page viewport.
  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setGalleryInView(entry.isIntersecting),
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Track which gallery card is snapped into view after scroll settles.
  useEffect(() => {
    const container = galleryRef.current;
    if (!container) return;
    const handleScrollEnd = () => {
      const card = galleryCardRefs.current[0];
      if (!card) return;
      const cardWidth = card.offsetWidth + 12; // card + gap-3
      const index = Math.round(container.scrollLeft / cardWidth);
      setGalleryPreviewIndex(Math.min(index, galleryCardRefs.current.length - 1));
    };
    container.addEventListener("scrollend", handleScrollEnd);
    return () => container.removeEventListener("scrollend", handleScrollEnd);
  }, []);

  // Snap to the opening index before paint (no animation).
  useLayoutEffect(() => {
    if (!overlayOpen) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = visibleIndex * el.clientHeight;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayOpen]);

  // Start playing as soon as a slide is ≥50% visible — mid-scroll, before snap settles.
  // root: scrollRef ensures ratios are measured against the container, not the page.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !overlayOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const index = slideRefs.current.indexOf(entry.target as HTMLDivElement);
          if (index === -1) continue;
          setVisibleIndex(index);
          break;
        }
      },
      { root: container, threshold: 0.5 },
    );

    slideRefs.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [overlayOpen]);

  useEffect(() => {
    document.body.style.overflow = overlayOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [overlayOpen]);

  useEffect(() => {
    if (!overlayOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" && visibleIndex < items.length - 1) scrollToIndex(visibleIndex + 1);
      else if (e.key === "ArrowUp" && visibleIndex > 0) scrollToIndex(visibleIndex - 1);
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [overlayOpen, visibleIndex, items.length]);


  return (
    <>
      <style>{`
        @keyframes reels-backdrop-fade {
          from { opacity: 0; }
          to   { opacity: 0.45; }
        }
        @keyframes reels-gallery-preview-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes reels-hint-fade {
          0%   { opacity: 0; transform: scale(0.75); }
          20%  { opacity: 1; transform: scale(1); }
          70%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1); }
        }
        .reels-scroll-container::-webkit-scrollbar { display: none; }
        /* Portrait mobile: full screen (slight zoom accepted). */
        .reels-clip { width: 100dvw; height: 100dvh; }
        /* Landscape mobile: height-constrained, width from 9:16. */
        @media (orientation: landscape) and (pointer: coarse) {
          .reels-clip { aspect-ratio: 9/16; width: auto; height: 100dvh; }
        }
        /* Desktop */
        @media (min-width: 768px) {
          .reels-clip { aspect-ratio: 9/16; width: auto; height: 92dvh; border-radius: 0.5rem; }
        }

        /* Controls visibility.
           Mobile portrait : absolute overlay buttons, no side columns.
           Landscape mobile + desktop : side columns, no overlay buttons. */
        .reels-ctrl-mobile  { display: flex; }
        .reels-ctrl-desktop { display: none; }
        .reels-row          { width: 100%; }
        @media (orientation: landscape) and (pointer: coarse) {
          .reels-ctrl-mobile  { display: none !important; }
          .reels-ctrl-desktop { display: flex; }
          .reels-row          { width: auto; }
        }
        @media (min-width: 768px) {
          .reels-ctrl-mobile  { display: none !important; }
          .reels-ctrl-desktop { display: flex; }
          .reels-row          { width: auto; }
        }
      `}</style>

      {/* Gallery strip — mobile: bleed to screen edge; desktop: respect container */}
      <div ref={galleryRef} className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar snap-x snap-mandatory">
        {galleryItems.map((item, i) => (
          <button
            key={i}
            ref={(el) => { galleryCardRefs.current[i] = el; }}
            onClick={() => openAt(i)}
            className="relative flex-none rounded-lg overflow-hidden focus:outline-none snap-start w-[230px] sm:w-[280px] md:w-[calc(25%-9px)]"
            style={{ aspectRatio: "9/16" }}
            aria-label={item.title}
          >
            {item.thumbnail ? (
              <Image src={item.thumbnail} alt="" fill className="object-cover" />
            ) : (
              <div className="absolute inset-0 bg-gray-800" />
            )}
            {galleryPreviewIndex === i && galleryInView && !overlayOpen && (item.hlsUrl || item.mp4Url) && (
              <video
                autoPlay
                muted
                playsInline
                loop
                preload="none"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ animation: "reels-gallery-preview-fade 0.4s ease forwards" }}
              >
                {item.hlsUrl && <source src={item.hlsUrl} type="application/vnd.apple.mpegurl" />}
                {item.mp4Url && <source src={item.mp4Url} type="video/mp4" />}
              </video>
            )}
          </button>
        ))}
      </div>

      {/* Gallery nav buttons — desktop only */}
      <div className="hidden md:flex justify-end gap-1 mt-2">
        <button
          onClick={() => scrollGallery("left")}
          disabled={galleryPos === "left"}
          className="rounded-sm h-10 w-10 flex items-center justify-center bg-sand-600 hover:bg-sand-700 disabled:opacity-30"
          aria-label="Föregående"
        >
          <Icon.AngleLeft />
        </button>
        <button
          onClick={() => scrollGallery("right")}
          disabled={galleryPos === "right"}
          className="rounded-sm h-10 w-10 flex items-center justify-center bg-sand-600 hover:bg-sand-700 disabled:opacity-30"
          aria-label="Nästa"
        >
          <Icon.AngleRight />
        </button>
      </div>

      {/* Fullscreen overlay — scroll container IS the fixed element (iOS Safari requires this) */}
      {overlayOpen && (
        <>
          {/* Scroll container — position:fixed directly (iOS Safari requires this).
              background:#000 via inline style so dark mode CSS variables cannot override it. */}
          <div
            ref={scrollRef}
            className="reels-scroll-container fixed inset-0"
            style={{
              zIndex: 50,
              background: "#000",
              overflowY: "scroll",
              scrollSnapType: "y mandatory",
              scrollbarWidth: "none",
              overscrollBehavior: "none",
            }}
          >
            {/* eslint-disable @next/next/no-img-element -- backdrop is decorative/blurred, never LCP */}
            {items.map((item, i) => (
              <div
                key={i}
                ref={(el) => { slideRefs.current[i] = el; }}
                style={{
                  height: "100dvh",
                  position: "relative",
                  overflow: "hidden",
                  scrollSnapAlign: "start",
                  scrollSnapStop: "always",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Blurred backdrop — desktop only, inside slide so it's guaranteed visible */}
                {Math.abs(i - visibleIndex) <= 1 && item.thumbnail && (
                  <img
                    src={item.thumbnail}
                    alt=""
                    className="hidden md:block"
                    style={{
                      position: "absolute", inset: 0, width: "100%", height: "100%",
                      objectFit: "cover", filter: "blur(40px)", transform: "scale(1.2)",
                      opacity: 0.45,
                    }}
                  />
                )}
                <div className="reels-clip" style={{ position: "relative", overflow: "hidden", zIndex: 1 }}>
                  {Math.abs(i - visibleIndex) <= 1 ? (
                    <ReelSlide
                      item={item}
                      isActive={visibleIndex === i}
                      preload={i === visibleIndex ? "auto" : "none"}
                      isMuted={isMuted}
                      onVideoRef={(el) => { videoRefs.current[i] = el; }}
                      onEnded={() => scrollToIndex(i + 1)}
                    />
                  ) : null}
                </div>
              </div>
            ))}

            {/* End screen */}
            <div
              ref={(el) => { slideRefs.current[items.length] = el; }}
              style={{
                height: "100dvh",
                overflow: "hidden",
                scrollSnapAlign: "start",
                scrollSnapStop: "always",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "1.5rem",
              }}
            >
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p style={{ color: "white", fontSize: "1.25rem", fontWeight: "bold", margin: 0, textAlign: "center" }}>
                Du är uppdaterad!
              </p>
              <button
                onClick={() => { setVisibleIndex(0); scrollToIndex(0, "instant"); }}
                style={{
                  display: "flex", alignItems: "center", gap: "0.5rem",
                  background: "rgba(255,255,255,0.15)",
                  color: "white", border: "none", borderRadius: "9999px",
                  padding: "0.625rem 1.375rem", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Börja om
              </button>
            </div>
          </div>
          {/* eslint-enable @next/next/no-img-element */}

          {/* Mobile controls — fixed above scroll container (portrait only) */}
          <div className="reels-ctrl-mobile fixed top-4 right-4 z-[51] items-center gap-2">
            <button
              onClick={() => setIsMuted(m => !m)}
              className="p-3 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}
              aria-label={isMuted ? "Slå på ljud" : "Stäng av ljud"}
            >
              {isMuted ? (
                <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M12 6l-6 4H3v4h3l6 4V6z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728" />
                </svg>
              )}
            </button>
            <button
              onClick={close}
              className="p-3 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}
              aria-label="Stäng"
            >
              <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Desktop + landscape mobile controls — transparent overlay with clip-sized spacer to align controls */}
          <div className="reels-ctrl-desktop fixed inset-0 z-[51] pointer-events-none items-center justify-center gap-4">
            <div className="pointer-events-auto flex flex-col gap-3">
              <button
                onClick={() => scrollToIndex(visibleIndex - 1)}
                disabled={visibleIndex === 0}
                className="p-3 rounded-full disabled:opacity-25 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}
                aria-label="Föregående"
              >
                <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                onClick={() => scrollToIndex(visibleIndex + 1)}
                disabled={visibleIndex === items.length - 1}
                className="p-3 rounded-full disabled:opacity-25 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}
                aria-label="Nästa"
              >
                <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {/* Transparent spacer matching clip dimensions — pushes controls to either side */}
            <div className="reels-clip" aria-hidden />
            <div className="pointer-events-auto flex flex-col gap-3 self-start mt-4">
              <button
                onClick={close}
                className="p-3 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}
                aria-label="Stäng"
              >
                <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <button
                onClick={() => setIsMuted(m => !m)}
                className="p-3 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}
                aria-label={isMuted ? "Slå på ljud" : "Stäng av ljud"}
              >
                {isMuted ? (
                  <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  <svg className="w-7 h-7" fill="none" stroke="white" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M12 6l-6 4H3v4h3l6 4V6z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}